import { useCallback, useEffect } from 'react'
import pluralize from 'pluralize'
import { cx, css, useTheme } from '@fable/theme'
import { Logo, Text } from '@fable/components'
import { Book, BookListItem } from '@fable/types'
import { AddNewIcon } from '../../assets/icons/components'
import { FlexBox, Button, TransitionElement } from '@fable/components'
import EyeIconCrossedOut from '../../assets/icons/components/EyeIconCrossedOut'
import {
  useActions,
  useSidebar,
  useTypedSelector,
  usePagination,
} from '../../hooks'
import { isFalseyObject } from '../../utils'
import BookListThumbnail from '../book_list_thumbnail/BookListThumbnail'
import Checkbox from '../checkbox/Checkbox'
import { BOOK_LIST_SAVE, BOOK_LIST_CREATE } from '../../app/store/constants'

const BooklistPicker = ({ book, bookId }: { book: Book; bookId: string }) => {
  const { profile } = useTypedSelector(({ user }) => user)
  const { bookListsPaginated, checkedBookLists, prependedBookLists } =
    useTypedSelector(({ books }) => books)
  const {
    getBookListPickerData,
    getBookListsPaginated,
    getBookListsPaginatedClear,
    setCheckedBookLists,
    prependBookListClear,
  } = useActions()
  const { onOpenSidebar, initialContent } = useSidebar()
  const { colors, mediaQueries } = useTheme()
  const { data, loadMore } = usePagination({
    apiCall: () =>
      getBookListsPaginated({ uuid: profile.data?.id || '', loadMore: true }),
    array: bookListsPaginated.data?.results,
    loading: bookListsPaginated.loading,
  })

  const styles = {
    container: css`
      width: 100%;
      background: ${colors.whiteFang};
      padding: 53px 53px 0;
      ${mediaQueries.mobileL} {
        min-height: 560px;
      }
    `,
    item: css`
      margin-bottom: 16px;
    `,
    checkbox: css`
      div.check-marker {
        border: 2px solid ${colors.blackSwan};
        margin-right: 0;
      }
    `,
    thumbnail: css`
      width: 88px;
      height: 88px;
      margin: 0px 8px;
      flex-shrink: 0;
    `,
    buttonOverride: css`
      justify-content: flex-start;
      align-items: center;
      height: fit-content;
      border: none;
      background: none;
      border-radius: 0;
      padding: 0;
      margin: 0;
      text-align: left;
      &:hover {
        border: none;
        background: none;
        border-radius: 0;
      }
    `,
    AddNewIcon: css`
      margin-left: 24px;
    `,
    loadMore: css`
      margin-top: 20px;
    `,
    loadMoreButton: css`
      width: fit-content;
    `,
  }

  const toggleSelected = (id: string) => {
    if (checkedBookLists.bookListIds?.includes(id)) {
      const filtered = checkedBookLists.bookListIds.filter((x) => x !== id)
      setCheckedBookLists({ book, bookId, bookListIds: filtered })
    } else {
      setCheckedBookLists({
        bookId,
        book,
        bookListIds: [...(checkedBookLists.bookListIds || []), id],
      })
    }
  }

  const clearData = useCallback(() => {
    setCheckedBookLists({ book: null, bookId: '', bookListIds: [] })
    getBookListsPaginatedClear()
    prependBookListClear()
  }, [getBookListsPaginatedClear, setCheckedBookLists, prependBookListClear])

  useEffect(
    () => () => {
      clearData()
    },
    [clearData]
  )

  useEffect(() => {
    if (
      profile.data?.id &&
      isFalseyObject(bookListsPaginated) &&
      initialContent === BOOK_LIST_SAVE
    ) {
      getBookListPickerData({ uuid: profile.data.id, bookId })
    }
  }, [
    bookId,
    bookListsPaginated,
    getBookListPickerData,
    initialContent,
    profile.data?.id,
  ])

  return (
    <div className={styles.container}>
      <button
        className={cx(styles.buttonOverride, styles.item, styles.AddNewIcon)}
        onClick={() => onOpenSidebar(BOOK_LIST_CREATE)}
      >
        <FlexBox alignItems="center">
          <AddNewIcon className={cx(styles.thumbnail)} />
          <Text type="body" sizing={{ base: 'M', mobileL: 'L' }}>
            New list
          </Text>
        </FlexBox>
      </button>
      <ul>
        {[...(prependedBookLists || []), ...(data || [])]
          .filter((x) => !!x)
          .map((item: BookListItem, i: number) => (
            <li key={i}>
              <TransitionElement
                transitionType="fadeIn"
                className={styles.item}
              >
                <button
                  className={styles.buttonOverride}
                  onClick={() => toggleSelected(item.id)}
                >
                  <FlexBox alignItems="center">
                    <Checkbox
                      isChecked={
                        checkedBookLists.bookListIds?.includes(item.id) === true
                      }
                      className={styles.checkbox}
                    />
                    <BookListThumbnail
                      key={i}
                      bookListData={item}
                      className={cx(
                        css`
                          padding: 6px;
                        `,
                        styles.thumbnail
                      )}
                    />
                    <div>
                      <FlexBox alignItems="center">
                        <Text type="body" sizing={{ base: 'M', mobileL: 'L' }}>
                          {item.name}
                        </Text>
                        {item.privacy !== 'everyone' && (
                          <>
                            &nbsp;
                            <EyeIconCrossedOut />
                          </>
                        )}
                      </FlexBox>
                      <Text type="label" sizing="L" color={colors.hurricane}>
                        {pluralize('book', item.count, true)}
                      </Text>
                    </div>
                  </FlexBox>
                </button>
              </TransitionElement>
            </li>
          ))}
        <li className={styles.loadMore}>
          <FlexBox centerAll>
            {bookListsPaginated.data?.next && !bookListsPaginated.loading && (
              <TransitionElement transitionType="fadeIn">
                <Button
                  negative
                  onClick={loadMore}
                  className={styles.loadMoreButton}
                >
                  Load more
                </Button>
              </TransitionElement>
            )}
            {bookListsPaginated.loading && (
              <TransitionElement transitionType="fadeIn">
                <Logo animate isIcon />
              </TransitionElement>
            )}
          </FlexBox>
        </li>
      </ul>
    </div>
  )
}

export default BooklistPicker
